import React, { useRef, useState } from 'react';
import { X, Download } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ProjectionsData } from '../types/projections';
import { formatCurrency } from '../utils/performanceMetrics';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface ProjectionsModalProps {
  data: ProjectionsData;
  onClose: () => void;
}

export function ProjectionsModal({ data, onClose }: ProjectionsModalProps) {
  const [isExporting, setIsExporting] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const chartData = Array.from({ length: 3 }, (_, i) => ({
    month: `Mês ${i + 1}`,
    conservador: data.scenarios.conservative[i].revenue,
    moderado: data.scenarios.moderate[i].revenue,
    agressivo: data.scenarios.aggressive[i].revenue,
  }));

  const calculateTotals = (scenarios: ProjectionsData['scenarios']['conservative']) => {
    return scenarios.reduce((acc, scenario) => ({
      revenue: acc.revenue + scenario.revenue,
      conversions: acc.conversions + scenario.conversions,
      roi: scenario.roi,
      cac: scenario.cac,
    }), { revenue: 0, conversions: 0, roi: 0, cac: 0 });
  };

  const conservativeTotals = calculateTotals(data.scenarios.conservative);
  const moderateTotals = calculateTotals(data.scenarios.moderate);
  const aggressiveTotals = calculateTotals(data.scenarios.aggressive);

  const getMonthlyROIs = (scenarios: ProjectionsData['scenarios']['conservative']) => {
    return scenarios.map(scenario => scenario.roi.toFixed(1) + '%').join(', ');
  };

  const handleExportPDF = async () => {
    if (!contentRef.current) return;

    setIsExporting(true);
    
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        ignoreElements: (element) => {
          return element.classList.contains('export-actions');
        }
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add title
      pdf.setFontSize(16);
      pdf.text('Projeções Financeiras - Próximos 3 Meses', 105, 15, { align: 'center' });
      
      // Add date
      pdf.setFontSize(10);
      pdf.setTextColor(128, 128, 128);
      const date = new Date().toLocaleDateString('pt-BR');
      pdf.text(`Gerado em: ${date}`, 20, 25);
      
      // Add the captured content
      pdf.addImage(imgData, 'PNG', 0, 30, imgWidth, imgHeight);
      
      pdf.save('projecoes-financeiras.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6" ref={contentRef}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Projeções Financeiras - Próximos 3 Meses</h2>
            <div className="flex gap-3 export-actions">
              <button 
                onClick={handleExportPDF}
                disabled={isExporting}
                className="inline-flex items-center px-4 py-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? 'Exportando...' : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Exportar Projeções
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Projeção de Receita por Cenário</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="conservador"
                    stroke={data.details.conservative.color}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="moderado"
                    stroke={data.details.moderate.color}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="agressivo"
                    stroke={data.details.aggressive.color}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Comparativo de Cenários</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Métrica
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cenário 1: 10% de Aumento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cenário 2: 20% de Aumento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cenário 3: 30% de Aumento
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Receita Total (3 meses)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(data.details.conservative.totalRevenue)}
                      <div className="text-xs text-gray-500 mt-1">
                        {data.details.conservative.advantages[1]}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(data.details.moderate.totalRevenue)}
                      <div className="text-xs text-gray-500 mt-1">
                        {data.details.moderate.advantages[1]}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(data.details.aggressive.totalRevenue)}
                      <div className="text-xs text-gray-500 mt-1">
                        {data.details.aggressive.advantages[1]}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ROI Médio (3 meses)
                    </td>
                    {[data.scenarios.conservative, data.scenarios.moderate, data.scenarios.aggressive].map((scenario, i) => (
                      <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{scenario[2].roi.toFixed(1)}%</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Mensal: {getMonthlyROIs(scenario)}
                        </div>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      CAC (Mês 3)
                    </td>
                    {[conservativeTotals, moderateTotals, aggressiveTotals].map((totals, i) => (
                      <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(totals.cac)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Nível de Risco
                    </td>
                    {[data.details.conservative, data.details.moderate, data.details.aggressive].map((scenario) => (
                      <td key={scenario.name} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {scenario.riskLevel}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[data.details.conservative, data.details.moderate, data.details.aggressive].map((scenario) => (
              <div key={scenario.name} className="bg-gray-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">{scenario.name}</h4>
                
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-green-600 mb-2">Vantagens</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {scenario.advantages.map((advantage, i) => (
                      <li key={i} className="text-sm text-gray-600">{advantage}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 className="text-sm font-medium text-red-600 mb-2">Desvantagens</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {scenario.disadvantages.map((disadvantage, i) => (
                      <li key={i} className="text-sm text-gray-600">{disadvantage}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}